import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import { axiosInstance } from '../helpers/axios'
import { ApplicantPortalDocument } from './useApplicantDocuments'

export interface TenantLeaseOffer {
    id: number
    kind: 'renewal' | 'new'
    lease: number | null
    lead: number | null
    proposed_rent: string
    proposed_term_months: number
    proposed_start_date: string
    proposed_end_date: string | null
    charge_lines?: Array<{
        id: number
        kind: 'base_rent' | 'premium' | 'concession'
        label: string
        amount: string
        sort_order: number
        premium: number | null
        concession: number | null
    }>
    unit_config: number | null
    unit_config_name: string | null
    bed_count: number | null
    bath_count: number | null
    pdf_url: string | null
    can_transfer: boolean
    concession: number | null
    concession_name: string | null
    concession_description: string | null
    area_id: number | null
    area_label: string | null
    area_config: number | null
    area_config_name: string | null
    unit: number | null
    unit_name: string | null
    offered_date: string | null
    expires_at: string | null
    status: string
    responded_at: string | null
}

export interface TransferUnitConfig {
    id: number
    name: string
    bed_count: number | null
    bath_count: number | null
}

export interface TransferRoommate {
    user_id: number
    name: string
}

export interface TransferOptions {
    unit_configs: TransferUnitConfig[]
    roommates: TransferRoommate[]
}

export interface TransferRequestPayload {
    desired_unit_config?: number | null
    roommate_ids?: number[]
    notes?: string
}

const BASE = 'concession/renewal-offers/tenant'

export const useTenantLeasingDocuments = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [documentList, setDocumentList] = useState<ApplicantPortalDocument[]>(
        [],
    )

    const getDocumentList = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await axiosInstance.get<{
                documents: ApplicantPortalDocument[]
            }>(`${BASE}/documents/`)
            const docs = res.data.documents ?? []
            setDocumentList(docs)
            return docs
        } catch {
            setDocumentList([])
            return []
        } finally {
            setIsLoading(false)
        }
    }, [])

    return { isLoading, documentList, getDocumentList }
}

export const useLeaseOffers = () => {
    const [offers, setOffers] = useState<TenantLeaseOffer[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [actingId, setActingId] = useState<number | null>(null)

    const getOffers = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await axiosInstance.get<TenantLeaseOffer[]>(`${BASE}/`)
            const data = Array.isArray(res.data) ? res.data : []
            setOffers(data)
            return data
        } catch {
            setOffers([])
            return []
        } finally {
            setIsLoading(false)
        }
    }, [])

    const acceptOffer = useCallback(async (id: number) => {
        setActingId(id)
        try {
            await axiosInstance.post(`${BASE}/${id}/accept/`)
            toast.success('Your offer has been accepted.')
            setOffers((current) => current.filter((offer) => offer.id !== id))
        } catch {
            toast.error('Unable to accept this offer.')
            throw new Error('accept failed')
        } finally {
            setActingId(null)
        }
    }, [])

    const declineOffer = useCallback(async (id: number, reason = '') => {
        setActingId(id)
        try {
            await axiosInstance.post(`${BASE}/${id}/decline/`, { reason })
            toast.success('Your response has been recorded.')
            setOffers((current) => current.filter((offer) => offer.id !== id))
        } catch {
            toast.error('Unable to decline this offer.')
            throw new Error('decline failed')
        } finally {
            setActingId(null)
        }
    }, [])

    const getTransferOptions = useCallback(async (id: number) => {
        try {
            const res = await axiosInstance.get<TransferOptions>(
                `${BASE}/${id}/transfer-options/`,
            )
            return res.data
        } catch {
            toast.error('Unable to load transfer options.')
            throw new Error('transfer options failed')
        }
    }, [])

    const requestTransfer = useCallback(
        async (id: number, payload: TransferRequestPayload) => {
            setActingId(id)
            try {
                await axiosInstance.post(`${BASE}/${id}/transfer/`, payload)
                toast.success(
                    'Your transfer request has been sent to the property team.',
                )
                setOffers((current) =>
                    current.filter((offer) => offer.id !== id),
                )
            } catch {
                toast.error('Unable to submit this transfer request.')
                throw new Error('transfer failed')
            } finally {
                setActingId(null)
            }
        },
        [],
    )

    return {
        offers,
        isLoading,
        actingId,
        getOffers,
        acceptOffer,
        declineOffer,
        getTransferOptions,
        requestTransfer,
    }
}
